/**
 * pages/Survey.jsx — Two-page survey wizard.
 *
 * Entry: /survey?experiment_id={id}            (submit new)
 *        /survey?experiment_id={id}&edit={sid} (edit existing submission)
 *
 * Page 1: Team, Role checkboxes, AI Tool checkboxes, Product Lifecycle checkboxes
 * Page 2: 6 star-rating questions + general_comments
 */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import apiClient from '../services/apiClient'
import Layout from '../components/Layout'
import NoticeBanner from '../components/NoticeBanner'
import FormSection from '../components/FormSection'
import CheckboxGroup from '../components/CheckboxGroup'
import TreeCheckboxGroup from '../components/TreeCheckboxGroup'
import StarRating from '../components/StarRating'
import Spinner from '../components/Spinner'

const INITIAL_RATINGS = {
  time_efficiency: '',
  quality_output: '',
  clarity_detail: '',
  risk_prevention: '',
  value_collaboration: '',
  repeat_refine: '',
}

export default function Survey() {
  const { user } = useAuth()
  const navigate   = useNavigate()
  const [searchParams] = useSearchParams()

  const experimentId = searchParams.get('experiment_id')
  const editSurveyId = searchParams.get('edit')
  const isEditMode   = !!editSurveyId

  // Redirect if no experiment selected
  useEffect(() => {
    if (!experimentId) navigate('/experiments', { replace: true })
  }, [experimentId, navigate])

  const [page, setPage] = useState(1)

  // Page 1 state
  const [teamId, setTeamId] = useState(user?.team_id ? String(user.team_id) : '')
  const [selectedRoleIds, setSelectedRoleIds]   = useState([])
  const [selectedToolIds, setSelectedToolIds]   = useState([])
  const [otherTools, setOtherTools]             = useState('')
  const [selectedModelIds, setSelectedModelIds] = useState([])
  const [otherModels, setOtherModels]           = useState('')
  const [selectedProductLifecycleIds, setSelectedProductLifecycleIds] = useState([])
  const [otherProductLifecycle, setOtherProductLifecycle]           = useState('')

  // Page 2 state
  const [ratings, setRatings]             = useState(INITIAL_RATINGS)
  const [comments, setComments]           = useState({})
  const [generalComments, setGeneralComments] = useState('')

  // UI state
  const [errors, setErrors]       = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess]       = useState(false)
  const [notOwner, setNotOwner]     = useState(false)

  // Reference data
  const [experiment, setExperiment] = useState(null)
  const [teams, setTeams]       = useState([])
  const [roles, setRoles]       = useState([])
  const [tools, setTools]       = useState([])
  const [providers, setProviders] = useState([])
  const [productLifecycles, setProductLifecycles] = useState([])
  const [questions, setQuestions] = useState([])
  const [loading, setLoading]   = useState(true)

  // Load reference data and (if edit mode) existing survey
  const loadRefData = useCallback(async () => {
    if (!experimentId) return
    setLoading(true)
    try {
      const [expRes, tmRes, roRes, tlRes, mdRes, phRes, quRes] = await Promise.all([
        apiClient.get(`/admin/experiments/${experimentId}`),
        apiClient.get('/admin/teams'),
        apiClient.get('/admin/survey-roles'),
        apiClient.get('/admin/ai-tools'),
        apiClient.get('/admin/ai-model-providers'),
        apiClient.get('/admin/product-lifecycles'),
        apiClient.get('/admin/questions'),
      ])
      setExperiment(expRes.data)
      setTeams(tmRes.data.items)
      setRoles(roRes.data.items)
      setTools(tlRes.data.items)
      setProviders(mdRes.data)
      setProductLifecycles(phRes.data.items)
      const activeQs = quRes.data.items
        .filter((q) => q.is_active)
        .sort((a, b) => a.display_order - b.display_order)
      setQuestions(activeQs)

      if (isEditMode) {
        const surveyRes = await apiClient.get(`/surveys/${editSurveyId}`)
        const s = surveyRes.data

        if (s.submitted_by_id !== user?.id) {
          setNotOwner(true)
          return
        }

        setTeamId(String(s.team_id ?? ''))

        // Map names → IDs using the loaded reference data
        const rolesAll  = roRes.data.items
        const toolsAll  = tlRes.data.items
        const modelsAll = mdRes.data.flatMap((p) => p.models)
        const productLifecyclesAll = phRes.data.items
        setSelectedRoleIds(s.selected_roles.map((name) => rolesAll.find((r) => r.name === name)?.id).filter(Boolean))
        setSelectedToolIds(s.selected_ai_tools.map((name) => toolsAll.find((t) => t.name === name)?.id).filter(Boolean))
        setOtherTools(s.other_ai_tools ?? '')
        setSelectedModelIds(s.selected_ai_models.map((name) => modelsAll.find((m) => m.name === name)?.id).filter(Boolean))
        setOtherModels(s.other_ai_models ?? '')
        setSelectedProductLifecycleIds(s.selected_product_lifecycles.map((name) => productLifecyclesAll.find((p) => p.name === name)?.id).filter(Boolean))
        setOtherProductLifecycle(s.other_product_lifecycle ?? '')
        setRatings({ ...INITIAL_RATINGS, ...s.ratings })
        setComments(s.comments ?? {})
        setGeneralComments(s.general_comments ?? '')
      }
    } catch {
      // silently log; user sees loading state
    } finally {
      setLoading(false)
    }
  }, [experimentId, editSurveyId, isEditMode])

  useEffect(() => { loadRefData() }, [loadRefData])

  const aiLeadRole = roles.find((r) => r.name === 'AI Lead')
  const isAiLead   = aiLeadRole && selectedRoleIds.includes(aiLeadRole.id)

  const toggleId = (setter, id) =>
    setter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])

  // ── Validation ───────────────────────────────────
  const validatePage1 = () => {
    const errs = {}
    if (!teamId)                                      errs.team     = 'Please select a team.'
    if (selectedRoleIds.length === 0)                 errs.roles    = 'Please select at least one role.'
    if (selectedToolIds.length === 0 && !otherTools.trim())
                                                      errs.tools    = 'Please select at least one AI tool or specify other.'
    if (selectedProductLifecycleIds.length === 0 && !otherProductLifecycle.trim())
                                                      errs.productLifecycles = 'Please select at least one product lifecycle stage or specify other.'
    return errs
  }

  const validatePage2 = () => {
    const errs = {}
    questions.forEach((q) => {
      if (!ratings[q.question_key]) errs[q.question_key] = 'Please provide a rating.'
    })
    if (isAiLead && !generalComments.trim())
      errs.general_comments = 'Experiment Outcome Notes are required when the AI Lead role is selected.'
    return errs
  }

  const handleNext = () => {
    const errs = validatePage1()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})
    setPage(2)
    window.scrollTo(0, 0)
  }

  const handleBack = () => {
    setErrors({})
    setPage(1)
    window.scrollTo(0, 0)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validatePage2()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})
    setSubmitting(true)

    const payload = {
      experiment_id:       Number(experimentId),
      team_id:             Number(teamId),
      selected_role_ids:   selectedRoleIds,
      selected_ai_tool_ids: selectedToolIds,
      other_ai_tools:      otherTools || null,
      selected_ai_model_ids: selectedModelIds,
      other_ai_models:     otherModels || null,
      selected_product_lifecycle_ids: selectedProductLifecycleIds,
      other_product_lifecycle:        otherProductLifecycle || null,
      ratings,
      comments,
      general_comments:    generalComments || null,
    }

    try {
      if (isEditMode) {
        await apiClient.put(`/surveys/${editSurveyId}`, payload)
      } else {
        await apiClient.post('/surveys', payload)
      }
      setSuccess(true)
      window.scrollTo(0, 0)
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || 'Submission failed. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }


  if (loading) {
    return (
      <Layout>
        <div className="loading-state">
          <Spinner size="md" />
          Loading survey…
        </div>
      </Layout>
    )
  }

  if (notOwner) {
    return (
      <Layout>
        <div className="alert alert-danger" style={{ maxWidth: 520, margin: '2rem auto' }}>
          You do not have permission to edit this survey response.
        </div>
      </Layout>
    )
  }

  if (success) {
    return (
      <Layout>
        <div className="card" style={{ maxWidth: 520, margin: '2rem auto', textAlign: 'center' }}>
          <h2 style={{ marginBottom: '0.5rem' }}>
            {isEditMode ? 'Survey updated!' : 'Thank you!'}
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            Your survey has been {isEditMode ? 'updated' : 'submitted'} successfully.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/results?experiment_id=${experimentId}`)}
            >
              View Results
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/experiments')}>
              Back to AI Briefs
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Edit-mode banner */}
      {isEditMode && (
        <NoticeBanner variant="warning">
          Editing your submission for AI Brief: <strong>{experiment?.experiment_name}</strong>
        </NoticeBanner>
      )}

      {/* AI Brief context header */}
      {experiment && (
        <NoticeBanner variant="info">
          <div className="survey-context-header">
            <h2>📋 AI Brief: "{experiment.experiment_name}"</h2>
            {experiment.project_info && <p>Project: {experiment.project_info}</p>}
          </div>
        </NoticeBanner>
      )}

      {errors.submit && <div className="alert alert-danger">{errors.submit}</div>}

      {page === 1 && (
        <form onSubmit={(e) => { e.preventDefault(); handleNext() }} noValidate>
          <FormSection title="Your Details">
            <div className="form-group">
              <label className="form-label">
                Your Name
              </label>
              <input
                type="text"
                className="form-control"
                value={user?.display_name ?? ''}
                readOnly
                style={{ background: 'var(--surface-alt)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label required" htmlFor="team-select">Team</label>
              <select
                id="team-select"
                className={`form-control${errors.team ? ' is-invalid' : ''}`}
                value={teamId}
                onChange={(e) => { setTeamId(e.target.value); setErrors((p) => ({ ...p, team: undefined })) }}
              >
                <option value="">— Select team —</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {errors.team && <span className="field-error">{errors.team}</span>}
            </div>
          </FormSection>

          <FormSection title="Your Role(s)" required>
            <CheckboxGroup
              items={roles}
              selectedIds={selectedRoleIds}
              onToggle={(id) => { toggleId(setSelectedRoleIds, id); setErrors((p) => ({ ...p, roles: undefined })) }}
              error={errors.roles}
            />
          </FormSection>

          <FormSection title="AI Tools Used" required>
            <CheckboxGroup
              items={tools}
              selectedIds={selectedToolIds}
              onToggle={(id) => { toggleId(setSelectedToolIds, id); setErrors((p) => ({ ...p, tools: undefined })) }}
              hasOther
              otherValue={otherTools}
              onOtherChange={(v) => { setOtherTools(v); setErrors((p) => ({ ...p, tools: undefined })) }}
              error={errors.tools}
            />
          </FormSection>

          <FormSection title="AI Models Used (optional)">
            <TreeCheckboxGroup
              providers={providers}
              selectedIds={selectedModelIds}
              onToggle={(id) => toggleId(setSelectedModelIds, id)}
              hasOther
              otherValue={otherModels}
              onOtherChange={setOtherModels}
            />
          </FormSection>

          <FormSection title="Product Lifecycle Stages" required>
            <CheckboxGroup
              items={productLifecycles}
              selectedIds={selectedProductLifecycleIds}
              onToggle={(id) => { toggleId(setSelectedProductLifecycleIds, id); setErrors((p) => ({ ...p, productLifecycles: undefined })) }}
              hasOther
              otherValue={otherProductLifecycle}
              onOtherChange={(v) => { setOtherProductLifecycle(v); setErrors((p) => ({ ...p, productLifecycles: undefined })) }}
              error={errors.productLifecycles}
            />
          </FormSection>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="submit" className="btn btn-primary btn-lg">
              Continue to Feedback →
            </button>
          </div>
        </form>
      )}

      {/* ── Page 2 ── */}
      {page === 2 && (
        <form onSubmit={handleSubmit} noValidate>
          {questions.map((q) => (
            <FormSection key={q.question_key} title={q.question_text} required>
              <StarRating
                questionKey={q.question_key}
                value={ratings[q.question_key]}
                onChange={(val) => { setRatings((prev) => ({ ...prev, [q.question_key]: val })); setErrors((p) => ({ ...p, [q.question_key]: undefined })) }}
                error={errors[q.question_key]}
              />
              <div className="form-group" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                <label className="form-label" htmlFor={`comment-${q.question_key}`}>
                  Comment (optional)
                </label>
                <textarea
                  id={`comment-${q.question_key}`}
                  className="form-control"
                  rows={2}
                  value={comments[q.question_key] ?? ''}
                  onChange={(e) =>
                    setComments((prev) => ({ ...prev, [q.question_key]: e.target.value }))
                  }
                />
              </div>
            </FormSection>
          ))}

          {/* General comments */}
          <FormSection title={isAiLead ? 'Experiment Outcome Notes' : 'Additional Comments'} required={isAiLead}>
            <textarea
              className={`form-control${errors.general_comments ? ' is-invalid' : ''}`}
              rows={4}
              value={generalComments}
              onChange={(e) => { setGeneralComments(e.target.value); setErrors((p) => ({ ...p, general_comments: undefined })) }}
              placeholder={isAiLead ? 'Required for AI Lead role…' : 'Optional…'}
            />
            {errors.general_comments && (
              <span className="field-error">{errors.general_comments}</span>
            )}
          </FormSection>

          {/* AI Brief summary footer block */}
          {experiment && (
            <div className="card" style={{ background: 'var(--surface-alt)', marginBottom: '1rem' }}>
              <p style={{ fontWeight: 700, marginBottom: '0.5rem' }}>AI Brief Summary</p>
              <table style={{ fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ paddingRight: '1rem', color: 'var(--text-secondary)', paddingBottom: '0.25rem' }}>AI Brief</td>
                    <td>{experiment.experiment_name}</td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: '1rem', color: 'var(--text-secondary)', paddingBottom: '0.25rem' }}>Your Name</td>
                    <td>{user?.display_name}</td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: '1rem', color: 'var(--text-secondary)' }}>Team</td>
                    <td>{teams.find((t) => String(t.id) === teamId)?.name ?? '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              ← Back
            </button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
              {submitting ? 'Submitting…' : isEditMode ? 'Update Feedback' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      )}
    </Layout>
  )
}
